import { useState } from "react";
import axios from "axios";
import { Upload, Loader2, CheckCircle, AlertCircle, Database, FileCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_TYPES = [
  { value: "socmint", label: "SOCMINT", desc: "Social media intelligence" },
  { value: "humint", label: "HUMINT", desc: "Human intelligence reports" },
  { value: "sigint", label: "SIGINT", desc: "Signals / network intelligence" },
  { value: "cyber_threat", label: "Cyber Threat", desc: "Cyber threat indicators" },
  { value: "geospatial", label: "Geospatial", desc: "Location-based observations" },
  { value: "imint", label: "IMINT", desc: "Imagery intelligence" },
];

function SocmintForm({ data, onChange }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Platform</Label>
          <Select value={data.platform} onValueChange={(v) => onChange("platform", v)}>
            <SelectTrigger className="bg-app border-slate-300"><SelectValue placeholder="Select platform" /></SelectTrigger>
            <SelectContent className="bg-white border-slate-300">
              <SelectItem value="twitter">Twitter / X</SelectItem>
              <SelectItem value="facebook">Facebook</SelectItem>
              <SelectItem value="reddit">Reddit</SelectItem>
              <SelectItem value="telegram">Telegram</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Author / Username</Label>
          <Input value={data.author} onChange={(e) => onChange("author", e.target.value)} placeholder="e.g. analyst_1" className="bg-app border-slate-300" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-slate-700">Content</Label>
        <Textarea value={data.content} onChange={(e) => onChange("content", e.target.value)} rows={4} placeholder="Paste the social media post content here..." className="bg-app border-slate-300" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-slate-700">Timestamp</Label>
        <Input type="datetime-local" value={data.timestamp} onChange={(e) => onChange("timestamp", e.target.value)} className="bg-app border-slate-300" />
      </div>
    </div>
  );
}

function HumintForm({ data, onChange }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Source Pseudonym</Label>
          <Input value={data.pseudonym} onChange={(e) => onChange("pseudonym", e.target.value)} placeholder="e.g. ECHO-7" className="bg-app border-slate-300" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Confidence (0 – 1)</Label>
          <Input type="number" min="0" max="1" step="0.05" value={data.confidence} onChange={(e) => onChange("confidence", e.target.value)} className="bg-app border-slate-300" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-slate-700">Report</Label>
        <Textarea value={data.report_text} onChange={(e) => onChange("report_text", e.target.value)} rows={4} placeholder="Describe the intelligence report..." className="bg-app border-slate-300" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Latitude</Label>
          <Input type="number" step="any" value={data.location_lat} onChange={(e) => onChange("location_lat", e.target.value)} placeholder="e.g. -25.987" className="bg-app border-slate-300" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Longitude</Label>
          <Input type="number" step="any" value={data.location_lon} onChange={(e) => onChange("location_lon", e.target.value)} placeholder="e.g. 28.112" className="bg-app border-slate-300" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-slate-700">Timestamp</Label>
        <Input type="datetime-local" value={data.timestamp} onChange={(e) => onChange("timestamp", e.target.value)} className="bg-app border-slate-300" />
      </div>
    </div>
  );
}

function SigintForm({ data, onChange }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Source IP</Label>
          <Input value={data.src_ip} onChange={(e) => onChange("src_ip", e.target.value)} placeholder="e.g. 192.168.1.50" className="bg-app border-slate-300" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Destination IP</Label>
          <Input value={data.dst_ip} onChange={(e) => onChange("dst_ip", e.target.value)} placeholder="e.g. 10.0.0.23" className="bg-app border-slate-300" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Source Port</Label>
          <Input type="number" value={data.src_port} onChange={(e) => onChange("src_port", e.target.value)} placeholder="e.g. 44322" className="bg-app border-slate-300" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Destination Port</Label>
          <Input type="number" value={data.dst_port} onChange={(e) => onChange("dst_port", e.target.value)} placeholder="e.g. 3389" className="bg-app border-slate-300" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Protocol</Label>
          <Select value={data.protocol} onValueChange={(v) => onChange("protocol", v)}>
            <SelectTrigger className="bg-app border-slate-300"><SelectValue placeholder="Select protocol" /></SelectTrigger>
            <SelectContent className="bg-white border-slate-300">
              <SelectItem value="TCP">TCP</SelectItem>
              <SelectItem value="UDP">UDP</SelectItem>
              <SelectItem value="ICMP">ICMP</SelectItem>
              <SelectItem value="HTTP">HTTP</SelectItem>
              <SelectItem value="HTTPS">HTTPS</SelectItem>
              <SelectItem value="DNS">DNS</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Timestamp</Label>
          <Input type="datetime-local" value={data.timestamp} onChange={(e) => onChange("timestamp", e.target.value)} className="bg-app border-slate-300" />
        </div>
      </div>
    </div>
  );
}

function CyberForm({ data, onChange }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Indicator Type</Label>
          <Select value={data.indicator_type} onValueChange={(v) => onChange("indicator_type", v)}>
            <SelectTrigger className="bg-app border-slate-300"><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent className="bg-white border-slate-300">
              <SelectItem value="ipv4-addr">IPv4 Address</SelectItem>
              <SelectItem value="domain-name">Domain Name</SelectItem>
              <SelectItem value="url">URL</SelectItem>
              <SelectItem value="file-hash">File Hash</SelectItem>
              <SelectItem value="email-addr">Email Address</SelectItem>
              <SelectItem value="malware">Malware</SelectItem>
              <SelectItem value="attack-pattern">Attack Pattern</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Indicator Value</Label>
          <Input value={data.indicator_value} onChange={(e) => onChange("indicator_value", e.target.value)} placeholder="e.g. 185.220.101.1" className="bg-app border-slate-300" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-slate-700">Description</Label>
        <Textarea value={data.description} onChange={(e) => onChange("description", e.target.value)} rows={3} placeholder="Describe the threat indicator..." className="bg-app border-slate-300" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Confidence (0 – 100)</Label>
          <Input type="number" min="0" max="100" value={data.confidence} onChange={(e) => onChange("confidence", e.target.value)} className="bg-app border-slate-300" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Timestamp</Label>
          <Input type="datetime-local" value={data.timestamp} onChange={(e) => onChange("timestamp", e.target.value)} className="bg-app border-slate-300" />
        </div>
      </div>
    </div>
  );
}

function GeospatialForm({ data, onChange }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Location Name</Label>
          <Input value={data.location_name} onChange={(e) => onChange("location_name", e.target.value)} placeholder="e.g. Border Post A" className="bg-app border-slate-300" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Observation Type</Label>
          <Select value={data.observation_type} onValueChange={(v) => onChange("observation_type", v)}>
            <SelectTrigger className="bg-app border-slate-300"><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent className="bg-white border-slate-300">
              <SelectItem value="movement">Movement</SelectItem>
              <SelectItem value="gathering">Gathering</SelectItem>
              <SelectItem value="infrastructure">Infrastructure</SelectItem>
              <SelectItem value="natural">Natural Feature</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Latitude</Label>
          <Input type="number" step="any" value={data.latitude} onChange={(e) => onChange("latitude", e.target.value)} placeholder="e.g. -25.987" className="bg-app border-slate-300" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Longitude</Label>
          <Input type="number" step="any" value={data.longitude} onChange={(e) => onChange("longitude", e.target.value)} placeholder="e.g. 28.112" className="bg-app border-slate-300" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-slate-700">Description</Label>
        <Textarea value={data.description} onChange={(e) => onChange("description", e.target.value)} rows={3} placeholder="Describe the observation..." className="bg-app border-slate-300" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-slate-700">Timestamp</Label>
        <Input type="datetime-local" value={data.timestamp} onChange={(e) => onChange("timestamp", e.target.value)} className="bg-app border-slate-300" />
      </div>
    </div>
  );
}

function ImintForm({ data, onChange }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Source</Label>
          <Select value={data.source} onValueChange={(v) => onChange("source", v)}>
            <SelectTrigger className="bg-app border-slate-300"><SelectValue placeholder="Select source" /></SelectTrigger>
            <SelectContent className="bg-white border-slate-300">
              <SelectItem value="satellite">Satellite</SelectItem>
              <SelectItem value="drone">Drone / UAV</SelectItem>
              <SelectItem value="aircraft">Aircraft</SelectItem>
              <SelectItem value="ground">Ground Photo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Observation</Label>
          <Input value={data.observation} onChange={(e) => onChange("observation", e.target.value)} placeholder="e.g. vehicle_convoy, building" className="bg-app border-slate-300" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Latitude</Label>
          <Input type="number" step="any" value={data.latitude} onChange={(e) => onChange("latitude", e.target.value)} placeholder="e.g. -25.987" className="bg-app border-slate-300" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Longitude</Label>
          <Input type="number" step="any" value={data.longitude} onChange={(e) => onChange("longitude", e.target.value)} placeholder="e.g. 28.112" className="bg-app border-slate-300" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-slate-700">Description</Label>
        <Textarea value={data.description} onChange={(e) => onChange("description", e.target.value)} rows={3} placeholder="Describe what is visible in the imagery..." className="bg-app border-slate-300" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-slate-700">Timestamp</Label>
        <Input type="datetime-local" value={data.timestamp} onChange={(e) => onChange("timestamp", e.target.value)} className="bg-app border-slate-300" />
      </div>
    </div>
  );
}

const FORM_DEFAULTS = {
  socmint: { platform: "twitter", author: "", content: "", timestamp: "" },
  humint: { pseudonym: "", report_text: "", location_lat: "", location_lon: "", confidence: "0.85", timestamp: "" },
  sigint: { src_ip: "", dst_ip: "", src_port: "", dst_port: "", protocol: "TCP", timestamp: "" },
  cyber_threat: { indicator_type: "ipv4-addr", indicator_value: "", description: "", confidence: "50", timestamp: "" },
  geospatial: { location_name: "", observation_type: "movement", latitude: "", longitude: "", description: "", timestamp: "" },
  imint: { source: "satellite", observation: "", latitude: "", longitude: "", description: "", timestamp: "" },
};

function buildPayload(sourceType, form) {
  switch (sourceType) {
    case "socmint":
      return {
        platform: form.platform,
        author: form.author,
        content: form.content,
        timestamp: form.timestamp ? new Date(form.timestamp).toISOString() : new Date().toISOString(),
      };
    case "humint":
      return {
        pseudonym: form.pseudonym,
        report_text: form.report_text,
        timestamp: form.timestamp ? new Date(form.timestamp).toISOString() : new Date().toISOString(),
        location: form.location_lat && form.location_lon ? {
          lat: parseFloat(form.location_lat),
          lon: parseFloat(form.location_lon),
        } : undefined,
        confidence: parseFloat(form.confidence),
      };
    case "sigint":
      return {
        src_ip: form.src_ip,
        dst_ip: form.dst_ip,
        src_port: form.src_port ? parseInt(form.src_port) : undefined,
        dst_port: form.dst_port ? parseInt(form.dst_port) : undefined,
        protocol: form.protocol,
        timestamp: form.timestamp ? new Date(form.timestamp).toISOString() : new Date().toISOString(),
      };
    case "cyber_threat":
      return {
        type: "stix_indicator",
        indicator_type: form.indicator_type,
        indicator_value: form.indicator_value,
        description: form.description,
        confidence: parseInt(form.confidence) || 50,
        timestamp: form.timestamp ? new Date(form.timestamp).toISOString() : new Date().toISOString(),
      };
    case "geospatial":
      return {
        type: "Feature",
        geometry: form.latitude && form.longitude ? {
          type: "Point",
          coordinates: [parseFloat(form.longitude), parseFloat(form.latitude)],
        } : undefined,
        properties: {
          name: form.location_name,
          observation_type: form.observation_type,
          description: form.description,
        },
        timestamp: form.timestamp ? new Date(form.timestamp).toISOString() : new Date().toISOString(),
      };
    case "imint":
      return {
        source: form.source,
        observation: form.observation,
        latitude: form.latitude ? parseFloat(form.latitude) : undefined,
        longitude: form.longitude ? parseFloat(form.longitude) : undefined,
        description: form.description,
        timestamp: form.timestamp ? new Date(form.timestamp).toISOString() : new Date().toISOString(),
      };
    default:
      return {};
  }
}

export default function ImsIngest() {
  const [sourceType, setSourceType] = useState("socmint");
  const [form, setForm] = useState(FORM_DEFAULTS.socmint);
  const [useJsonMode, setUseJsonMode] = useState(false);
  const [jsonPayload, setJsonPayload] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleSourceChange = (val) => {
    setSourceType(val);
    setForm(FORM_DEFAULTS[val] || FORM_DEFAULTS.socmint);
    setJsonPayload("");
    setResult(null);
  };

  const handleFieldChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);

    try {
      let payload;
      if (useJsonMode) {
        try {
          payload = JSON.parse(jsonPayload);
        } catch {
          toast.error("Invalid JSON — check your syntax");
          setSubmitting(false);
          return;
        }
      } else {
        payload = buildPayload(sourceType, form);
      }

      const res = await axios.post(`${API}/ims/ingest/${sourceType}`, {
        source_type: sourceType,
        payload: payload,
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      setResult(res.data);
      toast.success(`Ingested successfully — ${res.data.facts_created} fact(s) created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ingestion failed");
      setResult({ error: err.response?.data?.detail || "Ingestion failed" });
    }
    setSubmitting(false);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
            <Upload className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 tracking-wide">
              INGEST DATA
            </h1>
            <p className="text-slate-600">Submit raw intelligence from any source type</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Source Type */}
        <div className="bg-white border border-slate-200 p-6">
          <label className="block text-sm font-medium text-slate-700 mb-3">Source Type</label>
          <div className="flex items-center gap-3">
            <Select value={sourceType} onValueChange={handleSourceChange}>
              <SelectTrigger className="w-80 bg-app border-slate-300" data-testid="ims-source-type">
                <SelectValue placeholder="Select source type" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-300">
                {SOURCE_TYPES.map((st) => (
                  <SelectItem key={st.value} value={st.value}>
                    <span className="font-medium">{st.label}</span>
                    <span className="text-xs text-slate-500 ml-2">— {st.desc}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              type="button"
              onClick={() => setUseJsonMode(!useJsonMode)}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border transition-colors ${
                useJsonMode ? 'bg-emerald-50 text-emerald-600 border-emerald-300' : 'bg-app text-slate-500 border-slate-300'
              }`}
            >
              <FileCode className="w-3.5 h-3.5" />
              {useJsonMode ? "Switch to Simple Form" : "JSON Mode"}
            </button>
          </div>
        </div>

        {/* Fields or JSON */}
        <div className="bg-white border border-slate-200 p-6">
          <label className="block text-sm font-medium text-slate-700 mb-4">
            {useJsonMode ? "Payload (JSON)" : `${sourceType.toUpperCase()} — Enter Details`}
          </label>

          {useJsonMode ? (
            <textarea
              value={jsonPayload}
              onChange={(e) => setJsonPayload(e.target.value)}
              rows={14}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-300"
              placeholder='{"key": "value"}'
            />
          ) : (
            <>
              {sourceType === "socmint" && <SocmintForm data={form} onChange={handleFieldChange} />}
              {sourceType === "humint" && <HumintForm data={form} onChange={handleFieldChange} />}
              {sourceType === "sigint" && <SigintForm data={form} onChange={handleFieldChange} />}
              {sourceType === "cyber_threat" && <CyberForm data={form} onChange={handleFieldChange} />}
              {sourceType === "geospatial" && <GeospatialForm data={form} onChange={handleFieldChange} />}
              {sourceType === "imint" && <ImintForm data={form} onChange={handleFieldChange} />}
            </>
          )}
        </div>

        {/* Submit */}
        <div className="flex items-center gap-3">
          <Button
            type="submit"
            disabled={submitting}
            className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 rounded-none px-8"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" /> Ingest</>
            )}
          </Button>
        </div>
      </form>

      {/* Result */}
      {result && (
        <div className={`mt-6 p-4 border rounded-lg ${result.error ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
          <div className="flex items-center gap-2 mb-2">
            {result.error ? (
              <AlertCircle className="w-5 h-5 text-red-500" />
            ) : (
              <CheckCircle className="w-5 h-5 text-emerald-500" />
            )}
            <span className={`font-semibold ${result.error ? 'text-red-700' : 'text-emerald-700'}`}>
              {result.error ? 'Failed' : 'Ingested'}
            </span>
          </div>
          <pre className="text-sm text-slate-700 font-mono whitespace-pre-wrap">
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
